import React from 'react';
import { motion } from 'framer-motion';
import { TrendingDownIcon, InfoIcon, CheckIcon } from 'lucide-react';
import { CostBreakdown, EstimatedCosts } from './CostBreakdown';
import { EditableField } from './EditableField';
export type AccentColor = 'blue' | 'emerald' | 'amber' | 'violet';
export interface ScenarioData {
  id: string;
  title: string;
  rate: string;
  accent: AccentColor;
  paymentBreakdown: {
    newFirstAmount: number;
    newFirstPayment: number;
    mortgageInsurance: number;
    taxesInsurance: number;
    cashNeeded: number;
    cashToClose: number;
  };
  metrics: {
    monthlySavings: number;
    annualSavings: number;
    fiveYearSavings: number;
    thirtyYearSavings: number;
  };
  estimatedCosts: EstimatedCosts;
  additionalNotes?: {
    year2Payment: number;
    year2Savings: number;
    year3Payment: number;
    year3Savings: number;
  };
}
interface ScenarioCardProps {
  scenario: ScenarioData;
  index: number;
  currentTotalPayment: number;
  isSelected: boolean;
  onSelect: () => void;
  onChange: (path: string, value: any) => void;
  onCostChange: (key: keyof EstimatedCosts, value: number | null) => void;
}
const accentStyles = {
  blue: {
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    border: 'border-blue-200',
    badge: 'bg-blue-100 text-blue-800',
    ring: 'ring-blue-500',
    button: 'bg-blue-600 hover:bg-blue-700 text-white',
    buttonSelected: 'bg-blue-100 text-blue-800 border-blue-300'
  },
  emerald: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-200',
    badge: 'bg-emerald-100 text-emerald-800',
    ring: 'ring-emerald-500',
    button: 'bg-emerald-600 hover:bg-emerald-700 text-white',
    buttonSelected: 'bg-emerald-100 text-emerald-800 border-emerald-300'
  },
  amber: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-200',
    badge: 'bg-amber-100 text-amber-800',
    ring: 'ring-amber-500',
    button: 'bg-amber-600 hover:bg-amber-700 text-white',
    buttonSelected: 'bg-amber-100 text-amber-800 border-amber-300'
  },
  violet: {
    bg: 'bg-violet-50',
    text: 'text-violet-700',
    border: 'border-violet-200',
    badge: 'bg-violet-100 text-violet-800',
    ring: 'ring-violet-500',
    button: 'bg-violet-600 hover:bg-violet-700 text-white',
    buttonSelected: 'bg-violet-100 text-violet-800 border-violet-300'
  }
};
export function ScenarioCard({
  scenario,
  index,
  currentTotalPayment,
  isSelected,
  onSelect,
  onChange,
  onCostChange
}: ScenarioCardProps) {
  const styles = accentStyles[scenario.accent];
  const newTotalPayment = scenario.paymentBreakdown.newFirstPayment + scenario.paymentBreakdown.mortgageInsurance + scenario.paymentBreakdown.taxesInsurance;
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.5,
    delay: index * 0.15,
    ease: 'easeOut'
  }} className={`bg-white rounded-2xl shadow-sm overflow-hidden hover:shadow-md transition-all duration-300 flex flex-col h-full border-2 ${isSelected ? `border-${scenario.accent}-500 ring-4 ring-${scenario.accent}-500/20` : 'border-gray-200'}`} data-id="element-86">
      {/* Card Header */}
      <div className={`px-6 py-4 border-b ${styles.border} ${styles.bg} flex justify-between items-center transition-colors`} data-id="element-87">
        <EditableField value={scenario.title} type="text" onSave={val => onChange('title', val)} className={`font-bold text-lg ${styles.text}`} inputClassName="font-bold text-lg" data-id="element-88" />
        <div className={`px-3 py-1 rounded-full text-sm font-bold ${styles.badge}`} data-id="element-89">
          <EditableField value={scenario.rate} type="percent" onSave={val => onChange('rate', val)} className="font-bold" inputClassName="font-bold text-sm text-center" data-id="element-90" />
        </div>
      </div>

      <div className="p-6 flex-1 flex flex-col gap-8" data-id="element-91">
        {/* Hero Metrics */}
        <div className="grid grid-cols-2 gap-4" data-id="element-92">
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100" data-id="element-93">
            <p className="text-emerald-800 text-sm font-medium mb-1" data-id="element-94">
              Monthly Savings
            </p>
            <EditableField value={scenario.metrics.monthlySavings} type="currency" onSave={val => onChange('metrics.monthlySavings', val)} className="text-3xl font-bold text-emerald-600" inputClassName="text-3xl font-bold" data-id="element-95" />
          </div>
          <div className="bg-slate-50 rounded-xl p-4 border border-slate-200" data-id="element-96">
            <p className="text-slate-600 text-sm font-medium mb-1" data-id="element-97">
              Breakeven Point
            </p>
            <div className="flex items-baseline gap-1" data-id="element-98">
              <EditableField value={scenario.estimatedCosts.breakeven} type="number" decimals={2} onSave={val => onCostChange('breakeven', val)} className="text-3xl font-bold text-slate-800" inputClassName="text-3xl font-bold" data-id="element-99" />
              <span className="text-slate-500 font-medium" data-id="element-100">mo</span>
            </div>
          </div>
        </div>

        {/* New Loan Amount */}
        <div className="bg-slate-50 rounded-xl px-4 py-3 border border-slate-200 flex justify-between items-center" data-id="element-101">
          <span className="text-sm font-medium text-slate-600" data-id="element-102">
            New Loan Amount
          </span>
          <EditableField value={scenario.paymentBreakdown.newFirstAmount} type="currency" onSave={val => onChange('paymentBreakdown.newFirstAmount', val)} className="text-lg font-bold text-slate-800" inputClassName="text-lg font-bold" data-id="element-103" />
        </div>

        {/* Payment Comparison */}
        <div data-id="element-104">
          <h4 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wider" data-id="element-105">
            Payment Comparison
          </h4>
          <div className="flex items-center justify-between bg-gray-50 rounded-lg p-4 border border-gray-100" data-id="element-106">
            <div className="text-center" data-id="element-107">
              <p className="text-xs text-gray-500 uppercase font-semibold mb-1" data-id="element-108">
                Current
              </p>
              <p className="text-lg font-semibold text-gray-900" data-id="element-109">
                <EditableField value={currentTotalPayment} type="currency" onSave={() => {}} // Read-only here, controlled by Header
              className="cursor-default border-none hover:bg-transparent" data-id="element-110" />
              </p>
            </div>
            <div className="flex flex-col items-center px-4" data-id="element-111">
              <div className="bg-emerald-100 text-emerald-600 p-2 rounded-full mb-1" data-id="element-112">
                <TrendingDownIcon className="w-5 h-5" data-id="element-113" />
              </div>
              <span className="text-xs font-bold text-emerald-600" data-id="element-114">
                <EditableField value={scenario.metrics.monthlySavings} type="currency" onSave={val => onChange('metrics.monthlySavings', val)} data-id="element-115" />
              </span>
            </div>
            <div className="text-center" data-id="element-116">
              <p className="text-xs text-gray-500 uppercase font-semibold mb-1" data-id="element-117">
                New
              </p>
              <p className="text-lg font-bold text-emerald-600" data-id="element-118">
                <EditableField value={newTotalPayment} type="currency" onSave={() => {}} // Calculated value
              className="cursor-default border-none hover:bg-transparent" data-id="element-119" />
              </p>
            </div>
          </div>

          {/* Payment Details */}
          <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm" data-id="element-120">
            <div className="flex justify-between items-center" data-id="element-121">
              <span className="text-gray-500 flex items-center gap-1" data-id="element-122">
                P&I (
                <EditableField value={scenario.paymentBreakdown.newFirstAmount} type="currency" onSave={val => onChange('paymentBreakdown.newFirstAmount', val)} className="text-xs" data-id="element-123" />
                )
              </span>
              <EditableField value={scenario.paymentBreakdown.newFirstPayment} type="currency" onSave={val => onChange('paymentBreakdown.newFirstPayment', val)} className="font-medium text-gray-900" data-id="element-124" />
            </div>
            <div className="flex justify-between items-center" data-id="element-125">
              <span className="text-gray-500" data-id="element-126">Cash Needed</span>
              <EditableField value={scenario.paymentBreakdown.cashNeeded} type="currency" onSave={val => onChange('paymentBreakdown.cashNeeded', val)} className={`font-medium ${scenario.paymentBreakdown.cashNeeded > 0 ? 'text-red-600' : 'text-gray-900'}`} data-id="element-127" />
            </div>
            <div className="flex justify-between items-center" data-id="element-128">
              <span className="text-gray-500" data-id="element-129">Mortgage Ins.</span>
              <EditableField value={scenario.paymentBreakdown.mortgageInsurance} type="currency" onSave={val => onChange('paymentBreakdown.mortgageInsurance', val)} className="font-medium text-gray-900" data-id="element-130" />
            </div>
            <div className="flex justify-between items-center" data-id="element-131">
              <span className="text-gray-500" data-id="element-132">Cash to Close</span>
              <EditableField value={scenario.paymentBreakdown.cashToClose} type="currency" onSave={val => onChange('paymentBreakdown.cashToClose', val)} className="font-medium text-gray-900" data-id="element-133" />
            </div>
          </div>
        </div>

        {/* Savings Timeline */}
        <div data-id="element-134">
          <h4 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wider" data-id="element-135">
            Savings Timeline
          </h4>
          <div className="grid grid-cols-3 gap-2" data-id="element-136">
            <div className="bg-emerald-50/50 rounded-lg p-3 border border-emerald-100/50 text-center" data-id="element-137">
              <p className="text-xs text-emerald-700 font-medium mb-1" data-id="element-138">
                1 Year
              </p>
              <EditableField value={scenario.metrics.annualSavings} type="currency" onSave={val => onChange('metrics.annualSavings', val)} className="font-bold text-emerald-600" data-id="element-139" />
            </div>
            <div className="bg-emerald-50/50 rounded-lg p-3 border border-emerald-100/50 text-center" data-id="element-140">
              <p className="text-xs text-emerald-700 font-medium mb-1" data-id="element-141">
                5 Years
              </p>
              <EditableField value={scenario.metrics.fiveYearSavings} type="currency" onSave={val => onChange('metrics.fiveYearSavings', val)} className="font-bold text-emerald-600" data-id="element-142" />
            </div>
            <div className="bg-emerald-50/50 rounded-lg p-3 border border-emerald-100/50 text-center" data-id="element-143">
              <p className="text-xs text-emerald-700 font-medium mb-1" data-id="element-144">
                30 Years
              </p>
              <EditableField value={scenario.metrics.thirtyYearSavings} type="currency" onSave={val => onChange('metrics.thirtyYearSavings', val)} className="font-bold text-emerald-600" data-id="element-145" />
            </div>
          </div>
        </div>

        {/* Additional Notes (Scenario 3) */}
        {scenario.additionalNotes && <div className="bg-amber-50 rounded-lg p-3 border border-amber-200 flex items-start gap-3" data-id="element-146">
            <InfoIcon className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" data-id="element-147" />
            <div className="text-sm w-full" data-id="element-148">
              <p className="font-semibold text-amber-800 mb-1" data-id="element-149">
                Future Adjustments
              </p>
              <div className="grid grid-cols-2 gap-2 text-amber-900" data-id="element-150">
                <div data-id="element-151">
                  <span className="block text-xs opacity-80" data-id="element-152">
                    Year 2 Payment
                  </span>
                  <EditableField value={scenario.additionalNotes.year2Payment} type="currency" onSave={val => onChange('additionalNotes.year2Payment', val)} className="font-medium" data-id="element-153" />
                  <span className="text-xs ml-1 text-emerald-600 font-medium" data-id="element-154">
                    (Save{' '}
                    <EditableField value={scenario.additionalNotes.year2Savings} type="currency" onSave={val => onChange('additionalNotes.year2Savings', val)} data-id="element-155" />
                    )
                  </span>
                </div>
                <div data-id="element-156">
                  <span className="block text-xs opacity-80" data-id="element-157">
                    Year 3 Payment
                  </span>
                  <EditableField value={scenario.additionalNotes.year3Payment} type="currency" onSave={val => onChange('additionalNotes.year3Payment', val)} className="font-medium" data-id="element-158" />
                  <span className="text-xs ml-1 text-emerald-600 font-medium" data-id="element-159">
                    (Save{' '}
                    <EditableField value={scenario.additionalNotes.year3Savings} type="currency" onSave={val => onChange('additionalNotes.year3Savings', val)} data-id="element-160" />
                    )
                  </span>
                </div>
              </div>
            </div>
          </div>}

        {/* Cost Breakdown */}
        <div className="mt-auto pt-4" data-id="element-161">
          <CostBreakdown costs={scenario.estimatedCosts} onChange={onCostChange} data-id="element-162" />
        </div>

        {/* Action Button */}
        <div className="mt-2 pt-4 border-t border-gray-100" data-id="element-163">
          <button onClick={onSelect} className={`w-full py-3 px-4 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 ${isSelected ? `${styles.buttonSelected} border-2` : styles.button}`} data-id="element-164">
            {isSelected ? <>
                <CheckIcon className="w-5 h-5" data-id="element-165" />
                Selected Option
              </> : 'Select This Option'}
          </button>
        </div>
      </div>
    </motion.div>;
}